import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { Auth, Distributor } from "../api";
import {
  DistributorEmployeeLoginRequest,
  DistributorLoginRequest,
  GenerateOtpRequest,
  InvestorLoginRequest,
  SignupDistributorPayload,
  SignUpRequest,
  VerifyDistributorEmailArnPayload,
  DistributorEmployeeSetPasswordRequest,
  ResetPasswordRequest,
  distributorEmployeeLogin,
  distributorOtpGeneratePayload,
  isDistributorEmployeeLoginIdentifier,
  resetPassword,
  setDistributorEmployeePassword,
  toDistributorEmployeeLoginUsername,
} from "../api/auth";

export const getUserOptions = (enabled = true) =>
  queryOptions({
    queryKey: ["userInfo"],
    queryFn: () => Auth.me(),
    enabled,
  });

export function useUser(enabled: boolean = true) {
  return useQuery(getUserOptions(enabled));
}

export const useLoginAsDistributor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DistributorLoginRequest) => Auth.distributorLogin(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userInfo"] });
    },
  });
};

export const useResetPasswordDistributor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ResetPasswordRequest) => resetPassword(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userInfo"] });
    },
  });
};

export const useResetPasswordDistributorEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DistributorEmployeeSetPasswordRequest) =>
      setDistributorEmployeePassword(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userInfo"] });
    },
  });
};

export const useSetPasswordDistributorWithToken = () => {
  return useMutation({
    mutationFn: setDistributorEmployeePassword,
  });
};

/** One-shot login when opening set-password with a verification token (establishes session). */
export function useDistributorEmployeeVerificationLogin(
  verificationToken: string
) {
  const queryClient = useQueryClient();
  const trimmed = verificationToken.trim();
  const query = useQuery({
    queryKey: ["distributorEmployeeVerificationLogin", trimmed],
    queryFn: () => distributorEmployeeLogin({ verification_token: trimmed }),
    enabled: trimmed.length > 0,
    retry: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (query.isSuccess) {
      queryClient.invalidateQueries({ queryKey: ["userInfo"] }).catch(() => {});
    }
  }, [query.isSuccess, queryClient]);

  return query;
}

export const useLoginAsDistributorEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DistributorEmployeeLoginRequest) =>
      distributorEmployeeLogin(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userInfo"] });
    },
  });
};

export const useSignupAsDistributor = () => {
  return useMutation({
    mutationFn: (data: SignupDistributorPayload) =>
      Auth.signupDistributorApi(data),
  });
};

export const useGenerateOtp = () => {
  return useMutation({
    mutationFn: (data: GenerateOtpRequest) => Auth.generateOtp(data),
  });
};

/**
 * Distributor partner login, OTP request, and post-session password reset.
 * Chooses distributor vs employee APIs from the username shape (`.` = employee).
 */
export function useDistributorAuthActions() {
  const loginDistributor = useLoginAsDistributor();
  const loginEmployee = useLoginAsDistributorEmployee();
  const generateOtp = useGenerateOtp();
  const resetDistributor = useResetPasswordDistributor();
  const resetEmployee = useResetPasswordDistributorEmployee();

  const login = useCallback(
    async (arnOrUsername: string, passwordOrOtp: string) => {
      if (isDistributorEmployeeLoginIdentifier(arnOrUsername)) {
        await loginEmployee.mutateAsync({
          username: toDistributorEmployeeLoginUsername(arnOrUsername),
          password_or_otp: passwordOrOtp,
        });
      } else {
        await loginDistributor.mutateAsync({
          arn_code: arnOrUsername,
          password_or_otp: passwordOrOtp,
        });
      }
    },
    [loginDistributor, loginEmployee]
  );

  const requestOtp = useCallback(
    (
      arnOrUsername: string,
      options?: Parameters<typeof generateOtp.mutate>[1]
    ) => {
      generateOtp.mutate(distributorOtpGeneratePayload(arnOrUsername), options);
    },
    [generateOtp]
  );

  const requestOtpAsync = useCallback(
    (arnOrUsername: string) =>
      generateOtp.mutateAsync(distributorOtpGeneratePayload(arnOrUsername)),
    [generateOtp]
  );

  const setNewPassword = useCallback(
    async (arnOrUsername: string, newPassword: string) => {
      if (isDistributorEmployeeLoginIdentifier(arnOrUsername)) {
        await resetEmployee.mutateAsync({ new_password: newPassword });
      } else {
        await resetDistributor.mutateAsync({ new_password: newPassword });
      }
    },
    [resetDistributor, resetEmployee]
  );

  return {
    login,
    requestOtp,
    requestOtpAsync,
    setNewPassword,
    isLoginPending: loginDistributor.isPending || loginEmployee.isPending,
    isRequestOtpPending: generateOtp.isPending,
    isSetPasswordPending: resetDistributor.isPending || resetEmployee.isPending,
  };
}

export const useVerifyDistributorEmailArn = () => {
  return useMutation({
    mutationFn: (data: VerifyDistributorEmailArnPayload) =>
      Auth.verifyDistributorEmailArn(data),
  });
};

export const useRequestConsentToAddClient = () => {
  return useMutation({
    mutationFn: (data: { client_id: number }) =>
      Auth.requestConsentToAddClient(data),
  });
};

export const usePollForCreateAccountConsent = (
  enabled: boolean,
  clientId: number
) => {
  return useQuery({
    queryKey: ["pollForCreateAccountConsent", { client_id: clientId }],
    queryFn: () =>
      Distributor.checkConsentRequestToAddClientStatusApi(clientId),
    enabled,
    refetchInterval: 2000,
  });
};

export const useLoginAsInvestor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InvestorLoginRequest>) =>
      Auth.investorLogin(data),
    onSuccess: async () => {
      // Callers may have prefetched "clients"/"userInfo" before this
      // session existed (e.g. an ungated query firing on mount while
      // logged out), caching a stale empty/unauthenticated result under
      // the same query key a newly-authenticated investor reads right
      // after login — invalidate so the next read is guaranteed fresh.
      await queryClient.invalidateQueries({ queryKey: ["userInfo"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
};

export const useSignupAsInvestor = () => {
  return useMutation({
    mutationFn: (data: SignUpRequest) => Auth.register(data),
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => Auth.logout(),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["userInfo"] });
    },
  });
};

export const useCheckArnAndEmail = () => {
  return useMutation({
    mutationFn: (data: VerifyDistributorEmailArnPayload) =>
      Auth.verifyDistributorEmailArn(data),
  });
};

export const useResetDistributorPassword = () => {
  return useMutation({
    mutationFn: (data: { new_password: string }) =>
      Auth.resetDistributorPassword(data),
  });
};

export const useCreateAccountConsent = () => {
  return useMutation({
    mutationFn: Auth.verifyConsentApi,
  });
};

export const useRefreshToken = () => {
  return useMutation({
    mutationFn: (data: { refresh: string }) => Auth.refreshToken(data),
  });
};

export const useIsSuperAdmin = (adminArnCode: string[]) => {
  const { data: user } = useUser();
  return user?.username && adminArnCode.includes(user.username);
};
